/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable no-magic-numbers */
import { Api } from '@technology/web3d/dist/esnext/Api';
import { Checksum } from '@technology/web3d/dist/esnext/Helpers/Checksum';
import {
  Box3,
  BufferGeometry,
  CanvasTexture,
  Frustum,
  Mesh,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshPhongMaterial,
  Object3D,
  Plane,
  Sphere,
  Vector2,
  Vector3,
} from '@technology/web3d-plugin-threejs';
import { QuantizedMeshSettings } from './config/qm-settings';
import { LRUCache } from 'lru-cache';
import { PromiseQueue, QueueCapacityError } from './processing/promise-queue';
import { QmDecoder } from './processing/qm-decoder';
import { Vector3Const } from '@technology/web3d/dist/esnext/Helpers/common-utils.js';
import { Web3DMeshMaterial } from '@technology/web3d-plugin-threejs';
import { QuantizedMeshData } from './types/quantized-mesh-data';
import {
  constructGeometry,
  createGeometryFromBuffers,
  estimateMeshUploadBytes,
} from './processing/qm-factory';
import { getLogger } from '@trimble/logging';
import {
  addDisplayModeShader,
  getMeshMaterial,
  getTileMinMax,
} from './mesh-utils.js';
import { ObjectPool } from './object-pool';
import { TextureImage } from './types/texture-image';
import { TextureFetcher } from './types/quantized-mesh-options';
import { Quadrant } from './types/quadrant';
import { MeshCommitQueue } from './processing/mesh-commit-queue';

export declare type TileMesh = Mesh<
  BufferGeometry,
  MeshPhongMaterial | MeshNormalMaterial | Web3DMeshMaterial | MeshBasicMaterial
>;

type MeshTexture = {
  texture: CanvasTexture<OffscreenCanvas>;
  textureId: string;
};

const STALE_RENDERED_TEXTURE_PRIORITY_BOOST = 10_000;
const CAPACITY_RETRY_INITIAL_DELAY_MS = 100;
const CAPACITY_RETRY_MAX_DELAY_MS = 2_000;
const CAPACITY_RETRY_BACKOFF_MULTIPLIER = 2;
const CAPACITY_RETRY_MAX_ATTEMPT_EXPONENT = 5;
const CAPACITY_RETRY_JITTER_RATIO = 0.5;
const FALLBACK_REFINEMENT_PRIORITY_BOOST = 20_000;
const FALLBACK_REFINEMENT_PRIORITY_BOOST_DURATION_MS = 1_000;
const REFINEMENT_SCREEN_SIZE_FACTOR = 4;
const COARSENING_SCREEN_SIZE_FACTOR = 3;
const MOVING_REFINEMENT_SCREEN_SIZE_FACTOR = 6;
const MOVING_COARSENING_SCREEN_SIZE_FACTOR = 4.5;

class IncompleteTextureError extends Error {
  constructor(public readonly missingTextureFetcherIds: string[]) {
    super(
      `Texture generation missing layers: ${missingTextureFetcherIds.join(',')}`
    );
    this.name = 'IncompleteTextureError';
  }
}

type TileState =
  | 'start'
  | 'loading'
  | 'rendered'
  | 'refining'
  | 'refined'
  | 'ready'
  | 'unloading';

export class QMNode {
  public state: TileState = 'start';
  public isVisibleInFrame = false;
  public lastTouchedFrame = -1;
  /*
   * The mesh associated with this node.
   */
  public mesh: TileMesh | undefined | null;
  /**
   * @description The textureId associated with this node. This is used to determine if the texture applied to the mesh is still valid.
   */
  public textureId: string | undefined;
  private repaintInProgress: boolean = false;
  private repaintOperationId = 0;
  private repaintAbortController: AbortController | undefined;
  private pendingRepaintTextureId: string | undefined;
  private textureRequestAbortController: AbortController | undefined;
  private priorityBoostTextureId: string | undefined;
  private requestDeferredUntil = 0;
  private capacityRetryAttempts = 0;
  private fallbackRefinementPriorityBoostUntil = 0;
  private mayHaveQueuedRequests = false;
  /*
   * The children of this node.
   */
  public children: QMNode[] | undefined | null;
  /*
   * The bounding box of the tile within in the scene, which should be in the
   * project's coordinate system.
   */
  public readonly box: Box3;
  private mapModeBox: Box3;

  /*
   * The bounding box of the tile in the server's coordinate system.
   * For Quantized Mesh this is either in WGS84 (lat/lng) Web Mercator coordinates.
   */
  private readonly mapBox: Box3;

  private static readonly checksum = new Checksum();

  // private isTestTile(): boolean {
  //   // const t1 =
  //   //   this.zoomLevel === 17 &&
  //   //   this.tileIndex.x === 256638 &&
  //   //   this.tileIndex.y === 33849;
  //   return true;
  // }
  private readonly logger = getLogger();

  // This flag controls whether an array of debug information strings
  // is collected during the tile's lifecycle. This is invaluable for
  // troubleshooting complex loading and rendering issues. But it is a
  // memory leak if left on in production, so it is off by default.
  debugLog: boolean = false;

  constructor(
    private readonly api: Api,
    private readonly mapSettings: QuantizedMeshSettings,
    private readonly root: Object3D,
    private readonly zoomLevel: number,
    public tileIndex: Vector2,
    public parent: QMNode | null,
    private readonly imageCache: LRUCache<number, ImageBitmap>,
    private readonly qmDataCache: LRUCache<number, QuantizedMeshData>,
    private readonly qmDecoder: QmDecoder,
    private readonly objectPool: ObjectPool,
    private readonly textureQueues: Map<
      string,
      PromiseQueue<TextureImage>
    > = new Map(),
    private readonly qmDataPromiseQueue: PromiseQueue<QuantizedMeshData>,
    private readonly meshCommitQueue: MeshCommitQueue<TileMesh | undefined>,
    private readonly requestUpdate: (delayMs?: number) => void,
    private readonly initialBoundingBox?: Box3,
    private readonly textureQueueConcurrency = 8,
    private readonly textureQueueMaxQueuedLength: number | undefined = undefined
  ) {
    this.box = this.initialBoundingBox ?? new Box3();

    this.mapBox = new Box3();
    const { min, max } = this.getTileMinMax();

    this.mapMapBoxToScene(min, max);
    this.mapModeBox = this.createMapModeBox(this.box);
  }

  private createMapModeBox(box: Box3): Box3 {
    return new Box3(
      new Vector3(box.min.x, box.min.y, this.qmDecoder.baseHeight ?? 0.0),
      new Vector3(box.max.x, box.max.y, this.qmDecoder.baseHeight ?? 0.0)
    );
  }

  public dispose(): void {
    this.isVisibleInFrame = false;
    this.cancelRequests();
    this.removeMesh();
    this.children?.forEach((child) => {
      child.dispose();
    });
    this.children = [];
  }

  public get tileId(): string {
    return `${this.tileIndex.x}-${this.tileIndex.y}-${this.zoomLevel}`;
  }

  public get queueKeyFragment(): string {
    return `x${this.tileIndex.x}-y${this.tileIndex.y}-z${this.zoomLevel}`;
  }

  private getTileMinMax(): { min: Vector2; max: Vector2 } {
    return getTileMinMax(
      this.tileIndex,
      this.zoomLevel,
      this.mapSettings.mapProjection.bounds
    );
  }

  private async buildMeshGeometry(
    qmData: QuantizedMeshData,
    requestAbortController: AbortController
  ): Promise<{ geometry: BufferGeometry; localOrigin: Vector3 }> {
    const baseHeight = this.qmDecoder.baseHeight ?? 0.0;
    const initialBaseHeight = this.qmDecoder.initialBaseHeight ?? 0.0;

    this.box.min.z = qmData.header.minHeight;
    this.box.max.z = qmData.header.maxHeight;

    if (this.qmDecoder.isWorkerGeometryAvailable()) {
      const buffers = await this.qmDecoder.constructGeometryInWorker(
        {
          vertexData: qmData.vertexData,
          triangleIndices: qmData.triangleIndices,
          header: {
            minHeight: qmData.header.minHeight,
            maxHeight: qmData.header.maxHeight,
          },
          mapBox: {
            min: { x: this.mapBox.min.x, y: this.mapBox.min.y },
            max: { x: this.mapBox.max.x, y: this.mapBox.max.y },
          },
          baseHeight,
          initialBaseHeight,
        },
        {
          key: PromiseQueue.genKey(
            'qm-geometry',
            this.tileIndex.x,
            this.tileIndex.y,
            this.zoomLevel
          ),
          priority: () => this.tilePriority(),
          shouldRun: () => this.isRequestCurrent(requestAbortController),
          abortController: requestAbortController,
        }
      );

      if (buffers) {
        return createGeometryFromBuffers(buffers);
      }
      // A null result for a request that is no longer current means it was
      // aborted; building it on the main thread would be wasted work.
      if (!this.isRequestCurrent(requestAbortController)) {
        throw this.createAbortError();
      }
    }

    return constructGeometry(
      qmData,
      this.mapBox,
      this.mapSettings.mapToScene.bind(this.mapSettings),
      baseHeight,
      initialBaseHeight
    );
  }

  /**
   * True while `requestAbortController` is still the tile's live request and
   * the tile is still on screen. Checked before every stage that consumes a
   * shared worker or the render thread.
   */
  private isRequestCurrent(requestAbortController: AbortController): boolean {
    return (
      this.textureRequestAbortController === requestAbortController &&
      this.state === 'loading' &&
      this.isVisibleInFrame &&
      !requestAbortController.signal.aborted
    );
  }

  private createAbortError(): Error {
    const error = new Error(`Tile request aborted: ${this.tileId}`);
    error.name = 'AbortError';
    return error;
  }

  private createMesh(
    texture: CanvasTexture<OffscreenCanvas> | undefined,
    geometryResult: { geometry: BufferGeometry; localOrigin: Vector3 }
  ): TileMesh | undefined {
    if (!texture) {
      this.logger.info('No texture when creating mesh');
      return undefined;
    }

    // Bounds arrive precomputed from buildGeometryBuffers.
    const { geometry, localOrigin } = geometryResult;

    const material = getMeshMaterial(
      {
        appearance: 'web3d',
        opacity: this.mapSettings.options.meshSettings.opacity ?? 1.0,
      },
      this.api.renderingManager.uniforms
    ) as Web3DMeshMaterial;
    material.uniforms['displayMode'] = {
      value: this.mapSettings.displayMode === 'map' ? 1 : 0,
    };
    material.uniforms['progress'] = { value: 1.0 };
    material.uniforms['baseHeight'] = {
      value: this.qmDecoder.baseHeight,
    };
    addDisplayModeShader(material);

    if (material.type != 'MeshNormalMaterial') {
      material.map = texture;
      material.needsUpdate = true;
    }

    const mesh = new Mesh(geometry, material);
    // Set the mesh's world position to the tile's local origin
    mesh.position.copy(localOrigin);
    mesh.updateMatrixWorld();
    this.box.copy(geometry.boundingBox!);
    this.box.applyMatrix4(mesh.matrixWorld);
    this.mapModeBox = this.createMapModeBox(this.box);

    mesh.renderOrder = 1;

    // This is a band-aid to prevent very low-res tiles from z-fighting
    // with higher res tiles. Ideally these tiles wouldn't render, so
    // once that has been sorted this could possibly be removed.
    // Although it likely does make sense to render the root tile regardless
    // so that there's always a base texture, like with the TMP.
    if (this.parent === null || this.zoomLevel < 14) {
      mesh.renderOrder = 0; // Render first
      material.polygonOffset = true;
      material.polygonOffsetFactor = 1.0; // Push this tile's pixels away from the camera
      material.polygonOffsetUnits = 1.0;
    } else {
      mesh.renderOrder = 1; // Render on top of the root and low res tiles
      material.polygonOffset = true;
      material.polygonOffsetFactor = -1.0; // Pull this tile's pixels towards the camera
      material.polygonOffsetUnits = -1.0;
    }

    // We need to disable threejs frustum culling because of how 'map-mode' is implemented.
    // Map mode sets all the vertices to the surface base height in the shader, but threejs
    // isn't aware of this change, so it culls the tiles when the camera intersects the original
    // position of the vertices of that tile. We already do a frustum check and remove tiles that
    // are out of view, so this shouldn't cause any issues.
    mesh.frustumCulled = false;

    mesh.userData = {
      x: this.tileIndex.x,
      y: this.tileIndex.y,
      z: this.zoomLevel,
      node: this,
    };

    return mesh;
  }

  public removeMesh(): void {
    if (this.mesh) {
      this.cancelRepaint();
      this.mesh.visible = false;

      this.root.remove(this.mesh);
      this.mesh.removeFromParent();
      this.mesh.geometry.dispose();

      if (Array.isArray(this.mesh.material)) {
        this.mesh.material.forEach((mat) => {
          (mat as Web3DMeshMaterial).map?.dispose();
          (mat as Web3DMeshMaterial).dispose();
        });
      } else if (this.mesh.material) {
        (this.mesh.material as Web3DMeshMaterial).map?.dispose();
        this.mesh.material.dispose();
      }

      this.mesh = null;
    }
  }

  private readonly plane = new Plane();
  private readonly centerXYZ = this.vector3();

  private readonly sphere: Sphere = new Sphere();

  private checkFrustumVisibility(frustum: Frustum): boolean {
    // Quick check of bounding sphere first for better performance
    const box =
      this.mapSettings.displayMode === 'map' ? this.mapModeBox : this.box;
    return (
      frustum.intersectsSphere(box.getBoundingSphere(this.sphere)) &&
      frustum.intersectsBox(box)
    );
  }

  private cancelRequests(): void {
    const textureRequestAbortController = this.textureRequestAbortController;
    textureRequestAbortController?.abort();
    if (textureRequestAbortController) {
      this.qmDecoder.removeGeometryTasks(textureRequestAbortController);
    }
    this.textureRequestAbortController = undefined;
    this.resetCapacityRetryBackoff();
    this.resetFallbackRefinementPriorityBoost();

    if (textureRequestAbortController) {
      this.textureQueues.forEach((tq) => {
        tq.removeByAbortController(textureRequestAbortController, 'visibility');
      });
    }

    // Invisible branches are deactivated on every update, so skip the queue
    // scans unless this tile has enqueued work since it was last cancelled.
    if (!this.mayHaveQueuedRequests) {
      return;
    }
    this.mayHaveQueuedRequests = false;

    const textureKeyFragment = PromiseQueue.genKey(
      'img',
      this.tileIndex.x,
      this.tileIndex.y,
      this.zoomLevel
    );
    this.textureQueues.forEach((tq) => {
      tq.removeWhere(
        (task) => task.key.includes(textureKeyFragment),
        'visibility'
      );
    });
    this.qmDataPromiseQueue.remove(
      PromiseQueue.genKey(
        'qm',
        this.tileIndex.x,
        this.tileIndex.y,
        this.zoomLevel
      ),
      'visibility'
    );
  }

  /**
   * Unloads the tile, releasing all its associated resources. This includes
   * cancelling pending network requests, freeing GPU and CPU memory, and pending network requests for this tile.
   * recursively unloading all child tiles and
   */
  public unload(): void {
    // Prevent redundant work: If the tile is already unloaded or in the
    // process of unloading, there's nothing more to do. This prevents
    // unnecessary operations and potential errors from trying to release
    // resources that have already been freed.
    if (this.state === 'unloading' || this.state === 'start') {
      this.resetCapacityRetryBackoff();
      return;
    }

    // Transition the state to indicate
    // that this tile is now being cleaned up. This prevents the renderer
    // from trying to use it in the current or subsequent frames.
    this.transitionTo('unloading');

    // Cancel pending network requests
    this.nodeLog('unloading tile, cancelling pending requests');
    this.cancelRequests();

    //Reset to initial state: After all resources are freed, transition
    // the tile back to the START state. This makes the node a clean slate,
    // ready to be reused later if it comes back into view, which is more
    // efficient than destroying and recreating the node object itself.
    this.transitionTo('start');

    // The mesh data (vertex buffers, index buffers,
    // textures, etc.) stored in GPU memory must be explicitly released.
    // Failure to do so is a primary cause of memory leaks.
    if (this.mesh) {
      this.removeMesh();
    }

    // Release CPU-side data: Clear references to any large data structures
    // held in CPU memory, such as the decoded mesh data, to allow the
    // garbage collector to reclaim the memory.
    // - Nothing at present

    // Recursively unload children: A parent tile is the gateway to its
    // children. When a parent is unloaded, its entire subtree of descendants
    // must also be unloaded to ensure a complete cleanup.
    if (this.children) {
      for (const child of this.children) {
        child.unload();
      }
      this.children = [];
    }
  }

  public unloadChildren(): void {
    if (!this.children || this.children.length === 0) {
      return;
    }

    for (const child of this.children) {
      child.unload();
    }

    this.children = [];
  }

  private deactivateInvisibleBranch(): void {
    this.isVisibleInFrame = false;
    this.cancelRequests();

    if (!this.mesh && this.state !== 'refined') {
      this.transitionTo('start');
    }

    if (this.children) {
      for (const child of this.children) {
        child.deactivateInvisibleBranch();
      }
    }
  }

  /**
   * Transitions the tile to a new state and logs the change for debugging.
   * Centralizing state changes in this function makes it easy to trace the
   * lifecycle of a tile.
   *
   * @param {TileState} newState - The state to transition to.
   */
  public transitionTo(newState: TileState): void {
    if (this.state === newState) {
      return;
    }

    // console.log(
    //   `***Tile ${this.tileId}: State transition ${this.state} -> ${newState}`
    // );
    //
    this.nodeLog(`State transition ${this.state} -> ${newState}`);

    this.state = newState;
  }

  public calculateTileScreenSize(): number {
    if (!this.api.camera) {
      throw new Error('Camera not set on API');
    }
    const distance = this.api.camera.quaternion.equals(this.root.quaternion)
      ? this.plane
          .setFromNormalAndCoplanarPoint(
            Vector3Const.up.clone().applyQuaternion(this.root.quaternion),
            this.box.min
          )
          .distanceToPoint(this.api.camera.position)
      : this.api.camera.position.distanceTo(this.box.getCenter(this.centerXYZ));
    const sizeScreen = this.api.camera.getApproxPixelSize(
      this.box.min.distanceTo(this.box.max),
      distance
    );

    return sizeScreen;
  }

  /**
   * Ensures that the four child nodes for this tile exist. This method
   * lazily creates the child nodes the first time they are needed for
   * refinement. If the children have already been created, the function
   * does nothing.
   */
  private ensureChildrenExist(): void {
    if (this.children && this.children.length > 0) {
      return;
    }

    this.children = [];

    // Calculate the coordinates for the four child tiles based on the
    // parent's coordinates. In a standard quadtree tiling scheme, a tile
    // at (level, x, y) has four children at (level + 1).
    const childX1 = this.tileIndex.x * 2;
    const childX2 = this.tileIndex.x * 2 + 1;
    const childY1 = this.tileIndex.y * 2;
    const childY2 = this.tileIndex.y * 2 + 1;

    // Create the four child nodes (Southwest, Southeast, Northwest, Northeast).
    // Each new node is initialized with its correct coordinates and a
    // reference back to this parent node.
    const southwest = new QMNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX1, childY1),
      this,
      this.imageCache,
      this.qmDataCache,
      this.qmDecoder,
      this.objectPool,
      this.textureQueues,
      this.qmDataPromiseQueue,
      this.meshCommitQueue,
      this.requestUpdate,
      this.box.clone(),
      this.textureQueueConcurrency,
      this.textureQueueMaxQueuedLength
    );

    const southeast = new QMNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX2, childY1),
      this,
      this.imageCache,
      this.qmDataCache,
      this.qmDecoder,
      this.objectPool,
      this.textureQueues,
      this.qmDataPromiseQueue,
      this.meshCommitQueue,
      this.requestUpdate,
      this.box.clone(),
      this.textureQueueConcurrency,
      this.textureQueueMaxQueuedLength
    );

    const northwest = new QMNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX1, childY2),
      this,
      this.imageCache,
      this.qmDataCache,
      this.qmDecoder,
      this.objectPool,
      this.textureQueues,
      this.qmDataPromiseQueue,
      this.meshCommitQueue,
      this.requestUpdate,
      this.box.clone(),
      this.textureQueueConcurrency,
      this.textureQueueMaxQueuedLength
    );

    const northeast = new QMNode(
      this.api,
      this.mapSettings,
      this.root,
      this.zoomLevel + 1,
      new Vector2(childX2, childY2),
      this,
      this.imageCache,
      this.qmDataCache,
      this.qmDecoder,
      this.objectPool,
      this.textureQueues,
      this.qmDataPromiseQueue,
      this.meshCommitQueue,
      this.requestUpdate,
      this.box.clone(),
      this.textureQueueConcurrency,
      this.textureQueueMaxQueuedLength
    );

    this.children.push(southwest, southeast, northwest, northeast);
  }

  public update(
    frameNumber: number,
    frustum: Frustum,
    firstTilesRendered: boolean,
    isNavigating: boolean
  ): void {
    this.lastTouchedFrame = frameNumber;
    let isVisible = false;
    if (!firstTilesRendered) {
      isVisible = true;
    } else {
      isVisible = this.checkFrustumVisibility(frustum);
    }

    if (!isVisible) {
      this.deactivateInvisibleBranch();
      return;
    }

    this.isVisibleInFrame = true;
    const sizeScreen = this.calculateTileScreenSize();
    const screenSize = this.mapSettings.imageSize * devicePixelRatio;
    const refineThreshold =
      screenSize *
      (isNavigating
        ? MOVING_REFINEMENT_SCREEN_SIZE_FACTOR
        : REFINEMENT_SCREEN_SIZE_FACTOR);
    const coarsenThreshold =
      screenSize *
      (isNavigating
        ? MOVING_COARSENING_SCREEN_SIZE_FACTOR
        : COARSENING_SCREEN_SIZE_FACTOR);

    this.ensureLatestTexture();

    // Ensure data is loaded for visible tiles.
    if (this.state === 'start' && !this.mesh && this.canRetryTextureRequest()) {
      void this.requestData();
    } else if (this.state === 'start' && this.mesh) {
      this.transitionTo('ready');
    }

    const shouldRefine =
      sizeScreen > refineThreshold &&
      this.zoomLevel < this.mapSettings.maxZoomLevel;
    const shouldCoarsen = sizeScreen < coarsenThreshold;

    if (this.state === 'rendered' && shouldRefine) {
      this.transitionTo('refining');
    } else if (this.state === 'refining' && shouldCoarsen) {
      // Camera moved away, cancel refinement.
      this.transitionTo('rendered');
      this.children?.forEach((child) => child.deactivateInvisibleBranch());
    } else if (this.state === 'refined' && shouldCoarsen) {
      // The camera has zoomed out, and this parent tile is now sufficiently
      // detailed. We should render it instead of its children.
      if (!this.mesh) {
        this.logger.warn('No mesh in refined -> ready transition', this.tileId);
        this.transitionTo('start');
      } else {
        this.transitionTo('ready');
        this.children?.forEach((child) => child.deactivateInvisibleBranch());
      }
    }

    // Determine if we need to process children for this frame
    const shouldProcessChildren =
      this.state === 'refining' || this.state === 'refined';

    if (shouldProcessChildren) {
      this.ensureChildrenExist();

      for (const child of this.children!) {
        child.update(frameNumber, frustum, firstTilesRendered, isNavigating);
      }
    }

    if (
      this.state === 'refined' &&
      this.children?.some((c) => c.isVisibleInFrame === true && !c.mesh)
    ) {
      this.transitionTo('refining');
    }

    if (this.state === 'refining') {
      const allChildrenReady = this.children!.every(
        (child) =>
          !child.isVisibleInFrame ||
          child.state === 'ready' ||
          child.state === 'refined' ||
          child.state === 'rendered'
      );

      const anyChildVisible = this.children!.some(
        (child) => child.isVisibleInFrame
      );

      if (allChildrenReady && anyChildVisible) {
        this.transitionTo('refined');
      }
    }
  }

  private readonly log: string[] = [];

  async requestData(): Promise<void> {
    this.transitionTo('loading');
    this.nodeLog('requesting data');
    const requestAbortController = new AbortController();
    this.textureRequestAbortController = requestAbortController;
    try {
      const dataPromise = Promise.all([
        this.fetchAndDecodeMesh(requestAbortController)
          .then((data) => {
            this.nodeLog('mesh data fetched');
            return data;
          })
          .catch((e: unknown) => {
            if (this.isQueueCapacityError(e)) {
              this.nodeLog('mesh data request deferred by queue capacity');
            } else {
              this.nodeLog('error fetching mesh data: ' + (e as Error).message);
            }
            throw e;
          }),
        this.generateTexture(
          true,
          requestAbortController,
          this.mapSettings.textureId,
          () => this.tilePriority(),
          () =>
            this.textureRequestAbortController === requestAbortController &&
            this.state === 'loading' &&
            this.isVisibleInFrame &&
            !requestAbortController.signal.aborted
        )
          .then((texture) => {
            this.nodeLog('texture generated');
            return texture;
          })
          .catch((e: unknown) => {
            if (this.isQueueCapacityError(e)) {
              this.nodeLog('texture request deferred by queue capacity');
            } else {
              this.nodeLog('error generating texture: ' + (e as Error).message);
            }
            throw e;
          }),
      ]);
      const data = await dataPromise;
      this.nodeLog('mesh and texture promises resolved');
      try {
        // The tile may have been superseded or scrolled off screen while the
        // fetch was in flight; building its geometry would take a geometry
        // worker (or main-thread time) from a tile that is still visible.
        if (!this.isRequestCurrent(requestAbortController)) {
          this.nodeLog('tile no longer current, skipping geometry build');
          throw this.createAbortError();
        }
        const geometryResult = await this.buildMeshGeometry(
          data[0],
          requestAbortController
        );
        this.mesh = await this.meshCommitQueue.add({
          key: this.tileId,
          priority: () => this.tilePriority(),
          shouldRun: () => this.isRequestCurrent(requestAbortController),
          task: () => this.createMesh(data[1].texture, geometryResult),
          abortController: requestAbortController,
          uploadBytes: estimateMeshUploadBytes(
            geometryResult.geometry,
            data[1].texture
          ),
        });
      } catch (error) {
        data[1].texture?.dispose();
        throw error;
      }
      this.textureId = data[1].textureId;

      if (this.mesh) {
        this.nodeLog('mesh created successfully');
        this.resetCapacityRetryBackoff();
        this.transitionTo('ready');
        this.nodeLog('mesh created and tile ready');
        this.requestUpdate();
      } else {
        // If createMesh returns undefined, the tile will never become 'ready'.
        this.nodeLog('createMesh returned undefined, failing tile load');
        throw new Error('Mesh creation failed'); // Force into the catch block moving the tile back to 'start'
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        this.logger.debug(
          `[QMNode ${this.tileId}] Request aborted as expected.`
        );
      } else if (this.isRetryableTextureRequestError(e)) {
        this.logger.debug(
          `[QMNode ${this.tileId}] Request deferred for retry.`,
          e
        );
      } else {
        this.logger.error(
          `[QMNode ${this.tileId}] Error during requestData:`,
          e
        );
      }
      if (this.isRetryableTextureRequestError(e)) {
        this.nodeLog(`request deferred for retry: ${e.message}`);
        this.deferTextureRequestRetry();
      } else {
        this.nodeLog('error fetching data: ' + (e as Error).message);
      }
      this.abortRequestDataWork(requestAbortController);
      this.transitionTo('start');
      if (!this.isRetryableTextureRequestError(e)) {
        this.requestUpdate(CAPACITY_RETRY_INITIAL_DELAY_MS);
      }
    } finally {
      if (this.textureRequestAbortController === requestAbortController) {
        this.textureRequestAbortController = undefined;
      }
    }
  }

  private abortRequestDataWork(abortController: AbortController): void {
    abortController.abort();
    this.qmDecoder.removeGeometryTasks(abortController);
    this.qmDataPromiseQueue.removeByAbortController(
      abortController,
      'abortController'
    );
    this.textureQueues.forEach((queue) => {
      queue.removeByAbortController(abortController, 'abortController');
    });
  }

  private canRetryTextureRequest(): boolean {
    return performance.now() >= this.requestDeferredUntil;
  }

  private deferTextureRequestRetry(): void {
    const attemptExponent = Math.min(
      this.capacityRetryAttempts,
      CAPACITY_RETRY_MAX_ATTEMPT_EXPONENT
    );
    const baseDelay = Math.min(
      CAPACITY_RETRY_INITIAL_DELAY_MS *
        CAPACITY_RETRY_BACKOFF_MULTIPLIER ** attemptExponent,
      CAPACITY_RETRY_MAX_DELAY_MS
    );
    const jitter = baseDelay * CAPACITY_RETRY_JITTER_RATIO * Math.random();
    const delay = Math.min(baseDelay + jitter, CAPACITY_RETRY_MAX_DELAY_MS);

    this.capacityRetryAttempts = Math.min(
      this.capacityRetryAttempts + 1,
      CAPACITY_RETRY_MAX_ATTEMPT_EXPONENT
    );
    this.requestDeferredUntil = performance.now() + delay;
    this.nodeLog(`texture request retry deferred for ${Math.round(delay)}ms`);
    this.requestUpdate(delay);
  }

  private resetCapacityRetryBackoff(): void {
    this.requestDeferredUntil = 0;
    this.capacityRetryAttempts = 0;
  }

  /**
   * Callers must reprioritize the request queues afterwards; the model does
   * that once per update rather than once per boosted subtree.
   */
  public boostFallbackRefinementPrioritySubtree(): void {
    this.applyFallbackRefinementPriorityBoost();
    this.children?.forEach((child) => {
      if (child.isVisibleInFrame) {
        child.boostFallbackRefinementPrioritySubtree();
      }
    });
  }

  private applyFallbackRefinementPriorityBoost(): void {
    this.fallbackRefinementPriorityBoostUntil = Math.max(
      this.fallbackRefinementPriorityBoostUntil,
      performance.now() + FALLBACK_REFINEMENT_PRIORITY_BOOST_DURATION_MS
    );
    this.resetCapacityRetryBackoff();
  }

  private resetFallbackRefinementPriorityBoost(): void {
    this.fallbackRefinementPriorityBoostUntil = 0;
  }

  private isQueueCapacityError(error: unknown): error is QueueCapacityError {
    return error instanceof QueueCapacityError;
  }

  private isIncompleteTextureError(
    error: unknown
  ): error is IncompleteTextureError {
    return error instanceof IncompleteTextureError;
  }

  private isRetryableTextureRequestError(
    error: unknown
  ): error is QueueCapacityError | IncompleteTextureError {
    return (
      this.isQueueCapacityError(error) || this.isIncompleteTextureError(error)
    );
  }

  private async fetchAndDecodeMesh(
    abortController = new AbortController()
  ): Promise<QuantizedMeshData> {
    const cachedData = this.getCachedQuantizedMeshData();
    if (cachedData) {
      return cachedData;
    } else {
      this.mayHaveQueuedRequests = true;
      return this.qmDataPromiseQueue.add({
        priority: () => this.tilePriority(),
        cancelable: true,
        key: PromiseQueue.genKey(
          'qm',
          this.tileIndex.x,
          this.tileIndex.y,
          this.zoomLevel
        ),
        task: async () => {
          // Cache keys are tile coords only. Capture generation so a fetch
          // started under a previous filter cannot repopulate the cache after
          // invalidateTerrainData clears it (abort is checked in the queue
          // only after this task returns).
          const generation = this.qmDecoder.meshDataGeneration;
          const qmData = await this.qmDecoder.fetchAndDecode(
            { x: this.tileIndex.x, y: this.tileIndex.y, z: this.zoomLevel },
            abortController
          );
          if (
            abortController.signal.aborted ||
            generation !== this.qmDecoder.meshDataGeneration
          ) {
            const error = new Error(
              'Quantized mesh fetch aborted before cache write'
            );
            error.name = 'AbortError';
            throw error;
          }
          this.qmDataCache.set(this.getCacheKey(), qmData);

          return qmData;
        },
        abortController,
        shouldRun: () =>
          this.state === 'loading' &&
          this.isVisibleInFrame &&
          !abortController.signal.aborted,
      });
    }
  }

  public static cacheKey(x: number, y: number, z: number): number {
    return QMNode.checksum.clear().add(x).add(y).add(z).get();
  }

  public getCacheKey(): number {
    return QMNode.cacheKey(this.tileIndex.x, this.tileIndex.y, this.zoomLevel);
  }

  /* Tiles that are visible should have higher priority
   * with tiles closer to the camera getting even higher priority.
   * Lower number = higher priority
   */
  private readonly tempVector = new Vector3();
  public tilePriority(): number {
    let priority = 0;
    if (!this.isVisibleInFrame) {
      priority = 10000;
    }
    if (!this.api.camera) {
      return Number.MAX_VALUE;
    }
    const distance = this.api.camera.position.distanceTo(
      this.box.getCenter(this.tempVector)
    );

    priority += distance / 100;

    if (
      this.isVisibleInFrame &&
      performance.now() < this.fallbackRefinementPriorityBoostUntil
    ) {
      priority -= FALLBACK_REFINEMENT_PRIORITY_BOOST;
    }

    return priority;
  }

  private textureRequestPriority(): number {
    const priority = this.tilePriority();
    if (
      this.priorityBoostTextureId === this.mapSettings.textureId &&
      this.mesh &&
      this.textureId !== this.mapSettings.textureId
    ) {
      return priority - STALE_RENDERED_TEXTURE_PRIORITY_BOOST;
    }

    return priority;
  }

  public async fetchImage(
    tf: TextureFetcher,
    abortController: AbortController,
    cancelable: boolean = true,
    textureId = this.mapSettings.textureId,
    priority: () => number = () => this.tilePriority(),
    shouldRun: () => boolean = () => !abortController.signal.aborted
  ): Promise<TextureImage> {
    this.nodeLog(`fetchImage called for texture fetcher: ${tf.id}`);
    const cacheId = tf.cacheId(
      this.tileIndex.x,
      this.tileIndex.y,
      this.zoomLevel
    );
    const imageCacheKey = QMNode.checksum
      .clear()
      .add(textureId)
      .add(cacheId)
      .get();
    QMNode.checksum.clear();
    let img: ImageBitmap | null | undefined =
      this.imageCache.get(imageCacheKey);

    if (img) {
      return { img, opacity: tf.opacity, order: tf.order, id: tf.id };
    }

    if (this.zoomLevel > tf.maxZoom) {
      const { ancestor, path } = this.findAncestorWithTexture(tf.maxZoom);
      const ancestorImg = await ancestor.fetchImage(
        tf,
        abortController,
        cancelable,
        textureId,
        priority,
        shouldRun
      );

      const canvas = new OffscreenCanvas(256, 256);
      const ctx = canvas.getContext('2d');

      if (ancestorImg.img) {
        ctx!.globalAlpha = ancestorImg.opacity;
        ctx!.drawImage(ancestorImg.img, 0, 0);
      }

      const newCanvas = this.subsampleImage(canvas, [...path].reverse());
      img = await createImageBitmap(newCanvas);
      this.imageCache.set(imageCacheKey, img);

      return { img, opacity: tf.opacity, order: tf.order, id: tf.id };
    }

    if (!this.textureQueues.has(tf.id)) {
      this.textureQueues.set(
        tf.id,
        new PromiseQueue<TextureImage>(
          this.textureQueueConcurrency,
          `${tf.id}-texture`,
          this.textureQueueMaxQueuedLength
        )
      );
    }
    const queue = this.textureQueues.get(tf.id)!;
    const requestKey = `${PromiseQueue.genKey(
      `${tf.id}-img`,
      this.tileIndex.x,
      this.tileIndex.y,
      this.zoomLevel
    )}:${textureId}:${cacheId}`;
    this.mayHaveQueuedRequests = true;
    return queue.add({
      priority,
      cancelable,
      key: requestKey,
      abortController,
      shouldRun,
      task: async () => {
        this.nodeLog(`fetching image with fetcher: ${tf.id}`);
        if (!img) {
          img = await tf.fetcher(
            this.tileIndex.x,
            this.tileIndex.y,
            this.zoomLevel,
            abortController
          );

          this.imageCache.set(imageCacheKey, img!);
        }
        return { img, opacity: tf.opacity, order: tf.order, id: tf.id };
      },
    });
  }

  private subsampleImage(
    sourceCanvas: OffscreenCanvas,
    path: Quadrant[]
  ): OffscreenCanvas {
    let currentCanvas = sourceCanvas;

    // A map to easily get the coordinates for each quadrant.
    // Each quadrant is 128x128 pixels.
    const quadrants = {
      SW: { sx: 0, sy: 0 }, // South-West (bottom-left)

      SE: { sx: 128, sy: 0 }, // South-East (bottom-right)

      NW: { sx: 0, sy: 128 }, // North-West (top-left)

      NE: { sx: 128, sy: 128 }, // North-East (top-right)
    };

    for (const direction of path) {
      const quadrant = quadrants[direction];
      const resultCanvas = new OffscreenCanvas(256, 256);
      const resultCtx = resultCanvas.getContext('2d');

      if (!resultCtx) {
        throw new Error('Failed to get 2D context for texture subsampling');
      }

      // We draw a subsection of the current canvas onto the result canvas.
      resultCtx.drawImage(
        currentCanvas,
        quadrant.sx,
        quadrant.sy,
        128, // Source width (a 128px quadrant)
        128, // Source height (a 128px quadrant)
        0, // Destination X (start at top-left)
        0, // Destination Y (start at top-left)
        256, // Destination width (fill the canvas)
        256 // Destination height (fill the canvas)
      );

      // For the next iteration the result canvas becomes our new source.
      currentCanvas = resultCanvas;
    }

    return currentCanvas;
  }

  private findAncestorWithTexture(
    z: number,
    path: Quadrant[] = []
  ): { ancestor: QMNode; path: Quadrant[] } {
    if (this.zoomLevel > z) {
      if (this.tileIndex.x % 2 === 0) {
        if (this.tileIndex.y % 2 === 0) {
          path.push('SW');
        } else {
          path.push('NW');
        }
      } else {
        if (this.tileIndex.y % 2 === 0) {
          path.push('SE');
        } else {
          path.push('NE');
        }
      }

      return this.parent!.findAncestorWithTexture(z, path);
    } else {
      return { ancestor: this, path };
    }
  }

  private async generateTexture(
    cancelable: boolean,
    abortController = new AbortController(),
    textureId = this.mapSettings.textureId,
    priority: () => number = () => this.tilePriority(),
    shouldRun: () => boolean = () => !abortController.signal.aborted
  ): Promise<MeshTexture> {
    this.nodeLog('starting texture generation');
    const images = this.mapSettings.textureFetchers.map((tf) => {
      return this.fetchImage(
        tf,
        abortController,
        cancelable,
        textureId,
        priority,
        shouldRun
      );
    });

    this.nodeLog(`texture fetchers: ${textureId}`);

    try {
      const fetched = await Promise.all(images);
      const missingTextureFetcherIds = fetched
        .filter((r) => r.img == null)
        .map((r) => r.id);
      if (missingTextureFetcherIds.length > 0) {
        throw new IncompleteTextureError(missingTextureFetcherIds);
      }
      const results = fetched
        .filter((r) => r.img != null)
        .toSorted((a, b) => a.order - b.order);

      this.nodeLog(`fetched ${results.length} images for texture`);

      const canvas = new OffscreenCanvas(256, 256);
      const ctx = canvas.getContext('2d');

      results.forEach((result) => {
        ctx!.globalAlpha = result.opacity;
        ctx!.drawImage(result.img!, 0, 0);
      });
      const texture = new CanvasTexture<OffscreenCanvas>(canvas);
      // FlipY is true by default, so we need to set it to false to preserve the tile's Y orientation.
      texture.flipY = false;
      this.nodeLog('texture generation complete');

      return { texture, textureId };
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        this.logger.debug('texture generation aborted', e);
      } else if (this.isRetryableTextureRequestError(e)) {
        this.logger.debug('texture generation deferred for retry', e);
      } else {
        this.logger.warn('error during texture generation', e);
      }
      this.abortTextureGenerationWork(abortController);
      this.nodeLog('error during texture generation: ' + (e as Error).message);
      throw e;
    }
  }

  private abortTextureGenerationWork(abortController: AbortController): void {
    abortController.abort();
    this.textureQueues.forEach((queue) => {
      queue.removeByAbortController(abortController, 'abortController');
    });
  }

  private getCachedQuantizedMeshData(): QuantizedMeshData | undefined {
    return this.qmDataCache.get(this.getCacheKey());
  }

  /**
   * Maps the tile's bounding box to the scene's coordinate system.
   * This is necessary because the Quantized Mesh tiles are in WGS84 or Web Mercator coordinates,
   * while the scene may use a different coordinate system.
   *
   * @param tileMin - The minimum coordinates of the tile in the map projection.
   * @param tileMax - The maximum coordinates of the tile in the map projection.
   */
  private mapMapBoxToScene(tileMin: Vector2, tileMax: Vector2): void {
    this.mapBox.min.set(tileMin.x, tileMin.y, 0);
    this.mapBox.max.set(tileMax.x, tileMax.y, 0);
    const corners = [
      this.vector3().set(tileMin.x, tileMin.y, 0),
      this.vector3().set(tileMax.x, tileMin.y, 0),
      this.vector3().set(tileMin.x, tileMax.y, 0),
      this.vector3().set(tileMax.x, tileMax.y, 0),
    ];

    for (const corner of corners) {
      this.mapSettings.mapToScene(corner, corner);
      this.box.expandByPoint(corner);
    }
  }

  /**
   * Executes a callback function on the current node and all its descendants
   * that are currently considered "visible".
   *
   * In a Level of Detail (LOD) system like a quadtree, a node is considered
   * the "visible" one if its own mesh is set to visible, implying its children are not.
   * This function traverses the tree and finds these specific nodes.
   *
   * @param fn The function to execute for each visible node found.
   */
  public traverseVisible(fn: (node: QMNode) => void): void {
    // A node is the one we want to act on if its mesh exists and is visible.
    // This signifies it's a "leaf" of the visible part of the tree.
    if (this.mesh?.visible) {
      fn(this);
      // We stop here and don't traverse to children, because if the parent
      // tile is visible, its children should not be.
      return;
    }

    // If this node's mesh isn't visible, its children might be.
    // Continue the traversal down the tree.
    if (this.children) {
      for (const child of this.children) {
        child.traverseVisible(fn);
      }
    }
  }

  /*
   * Executes a callback function on the current node and all its descendants.
   * @param fn The function to execute for each node in the tree.
   */
  public traverseAll(fn: (node: QMNode) => void): void {
    fn(this);
    if (this.children) {
      for (const child of this.children) {
        child.traverseAll(fn);
      }
    }
  }

  public traverseDescendantsWithDepth(
    fn: (node: QMNode, depth: number) => void,
    depth = 0
  ): void {
    fn(this, depth);
    if (this.children) {
      for (const child of this.children) {
        child.traverseDescendantsWithDepth(fn, depth + 1);
      }
    }
  }

  /*
   * Sets the display mode of the tile's material to either 'terrain' or 'map'.
   * This changes how the tile is rendered, either as a 3D terrain or a flat map.
   * @param mode - The display mode to set ('terrain' or 'map').
   * @param currentProgress - The current progress of the transition (0 to 1).
   */
  public setDisplayMode(
    mode: 'terrain' | 'map',
    currentProgress: number
  ): void {
    if (this.mesh && this.mesh.material) {
      (this.mesh.material as Web3DMeshMaterial).uniforms['progress'] = {
        value: currentProgress,
      };

      const displayModeValue = mode === 'terrain' ? 0 : 1;
      (this.mesh.material as Web3DMeshMaterial).uniforms['displayMode'] = {
        value: displayModeValue,
      };

      (this.mesh.material as Web3DMeshMaterial).uniforms['baseHeight'] = {
        value: this.qmDecoder.baseHeight,
      };
    }
  }

  private applyTexture(meshTexture: MeshTexture): void {
    if (!this.mesh) {
      meshTexture.texture.dispose();
      return;
    }
    this.resetCapacityRetryBackoff();

    if (
      this.mesh.material &&
      this.mesh.material.type !== 'MeshNormalMaterial'
    ) {
      const material = this.mesh.material as Web3DMeshMaterial;

      if (material.map) {
        material.map.dispose();
      }
      material.map = meshTexture.texture;
      this.mesh.material.needsUpdate = true;
    } else {
      meshTexture.texture.dispose();
    }
    this.textureId = meshTexture.textureId;
  }

  private isRepaintPendingFor(textureId: string): boolean {
    return this.repaintInProgress && this.pendingRepaintTextureId === textureId;
  }

  private beginRepaint(textureId: string): {
    abortController: AbortController;
    generation: number;
  } {
    this.repaintAbortController?.abort();
    const abortController = new AbortController();
    const generation = ++this.repaintOperationId;
    this.repaintAbortController = abortController;
    this.pendingRepaintTextureId = textureId;
    this.repaintInProgress = true;

    return { abortController, generation };
  }

  private isRepaintCurrent(
    generation: number,
    textureId: string,
    abortController: AbortController
  ): boolean {
    return (
      this.repaintOperationId === generation &&
      this.pendingRepaintTextureId === textureId &&
      this.repaintAbortController === abortController &&
      this.mapSettings.textureId === textureId &&
      this.mesh != null &&
      !abortController.signal.aborted
    );
  }

  private finishRepaint(generation: number): void {
    if (this.repaintOperationId !== generation) {
      return;
    }

    this.priorityBoostTextureId = undefined;
    this.repaintAbortController = undefined;
    this.pendingRepaintTextureId = undefined;
    this.repaintInProgress = false;
  }

  private cancelRepaint(): void {
    const repaintAbortController = this.repaintAbortController;
    repaintAbortController?.abort();
    if (repaintAbortController) {
      this.textureQueues.forEach((queue) => {
        queue.removeByAbortController(
          repaintAbortController,
          'abortController'
        );
      });
    }
    this.priorityBoostTextureId = undefined;
    this.repaintAbortController = undefined;
    this.pendingRepaintTextureId = undefined;
    this.repaintInProgress = false;
    this.repaintOperationId++;
  }

  /*
   * Repaints the tile by regenerating its texture and applying it to the mesh.
   */
  public ensureLatestTexture(prioritizeStaleRenderedTile = false): void {
    if (
      prioritizeStaleRenderedTile &&
      this.textureId !== this.mapSettings.textureId
    ) {
      this.priorityBoostTextureId = this.mapSettings.textureId;
    }

    if (
      this.mesh &&
      this.textureId !== this.mapSettings.textureId &&
      this.canRetryTextureRequest() &&
      !this.isRepaintPendingFor(this.mapSettings.textureId)
    ) {
      void this.repaint();
    }
  }

  public async repaint(prioritizeStaleRenderedTile = false): Promise<void> {
    if (!this.mesh || this.textureId === this.mapSettings.textureId) {
      return;
    }

    const targetTextureId = this.mapSettings.textureId;
    if (prioritizeStaleRenderedTile) {
      this.priorityBoostTextureId = targetTextureId;
    }

    if (this.isRepaintPendingFor(targetTextureId)) {
      return;
    }

    const { abortController, generation } = this.beginRepaint(targetTextureId);
    try {
      this.nodeLog('repainting tile');
      const meshTexture = await this.generateTexture(
        true,
        abortController,
        targetTextureId,
        () => this.textureRequestPriority(),
        () =>
          this.isRepaintCurrent(generation, targetTextureId, abortController)
      );
      if (
        !this.isRepaintCurrent(generation, targetTextureId, abortController)
      ) {
        meshTexture.texture.dispose();
        return;
      }

      this.applyTexture(meshTexture);
      const container = this.api.container;
      if (
        container &&
        container.clientWidth > 0 &&
        container.clientHeight > 0
      ) {
        this.api.renderingManager?.redraw();
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        this.logger.debug(`Tile ${this.tileId}: Repaint aborted.`, e);
      } else if (this.isRetryableTextureRequestError(e)) {
        this.logger.debug(
          `Tile ${this.tileId}: Repaint deferred for retry.`,
          e
        );
        this.deferTextureRequestRetry();
      } else {
        // Non-retryable: stay idle with the stale texture. Scheduling
        // requestUpdate here would loop — textureId stays stale, so the next
        // update would call ensureLatestTexture → repaint again.
        getLogger().error(`Tile ${this.tileId}: Failed to repaint tile.`, e);
      }
    } finally {
      this.finishRepaint(generation);
    }
  }

  public countTilesInTree(): number {
    let count = 1; // Count this node
    if (this.children) {
      for (const child of this.children) {
        count += child.countTilesInTree(); // Recursively count children
      }
    }
    return count;
  }

  /*
   * If debug logging is enabled, appends a log entry to the tile's log array.
   * This is useful for tracing the tile's lifecycle and diagnosing issues.
   * @param log - The log entry to append.
   */
  private nodeLog(log: string): void {
    if (this.debugLog) {
      this.log.push(log);
    }
  }

  private vector3(): Vector3 {
    // Taking out for now as it can cause subtle and not so subtle bugs (discovered while using vector2)
    // The main thing to avoid is using a pooled vector asn a property of any persistent class,
    // as when the pool hands it out again, the value will change.
    // return this.objectPool.getVector3();
    return new Vector3();
  }
}
